import { action, debug } from 'easy-peasy';
import authLocalActions from '@/stores/auth/authLocalActions';
import constants from '@/config/constants';
import { isFeatureEnabled } from '@/contexts/EventsProvider/posthogClient';
import { FeatureFlag } from '@/contexts/EventsProvider/featureFlags';
import cloneDeep from 'lodash/cloneDeep';
import chunk from 'lodash/chunk';
import flattenDeep from 'lodash/flattenDeep';
import extend from 'lodash/extend';
import { Platform } from 'react-native';
import { StoreModel } from '@/stores/store';
import {
  ExerciseType,
  Patient,
  Subscription,
  UserEpa,
} from '@/stores/store.types';

import { logError } from '@/utils/logHandling';
import { UserConsent } from '@/stores/state/globalState';
import selfTrainingModel from '@/stores/state/selfTrainingSelection';
import { TherapistStackParamList } from '@/navigations/TherapistStack';
import { Route } from '@react-navigation/native';
import { IS_WEB } from '@/utils/platform';

export interface UpdateSettingPayload {
  exerciseName: 'Lesen' | 'Sprechen' | 'Verstehen' | 'Schreiben';
  newValue: string | number;
  setType: 'wordset' | 'sentenceset' | 'textset';
  fieldToBeUpdated: 'setting' | 'value' | 'playbackRate';
  textAudioSpeedSettings?: boolean;
}

//  Shuffle Array - this was created because if I use the one from utils it will show a cycle warning
function shuffle(array: unknown[]) {
  let currentIndex = array.length,
    temporaryValue,
    randomIndex;

  // While there remain elements to shuffle...
  while (0 !== currentIndex) {
    // Pick a remaining element...
    randomIndex = Math.floor(Math.random() * currentIndex);
    currentIndex -= 1;

    // And swap it with the current element.
    temporaryValue = array[currentIndex];
    array[currentIndex] = array[randomIndex];
    array[randomIndex] = temporaryValue;
  }

  return array;
}

const trackPracticeSessionDefault = {
  startDate: '',
  endDate: '',
  os: IS_WEB ? 'browser' : Platform.OS,
  app: constants.appMode,
  appVersion: constants.appVersion,
  uuid: '',
};

export type LocalActions = typeof localActions;

const localActions = {
  setPatientCode: action<StoreModel, string>((state, payload) => {
    if (state.patient) {
      state.patient.code = payload;
    }
  }),

  changePatientActiveSet: action<StoreModel, string>(
    (state, payload: string) => {
      state.patientActiveSet = payload;
    },
  ),

  changeActivePatient: action<StoreModel, unknown>((state, payload) => {
    const { data } = payload;
    state.activePatient = data;
  }),

  changeSelfTrainingSelection: action<
    StoreModel,
    typeof selfTrainingModel.selfTrainingSelection
  >((state, payload) => {
    state.selfTrainingSelection = payload;
  }),

  resetSelfTrainingSelection: action<StoreModel, void>((state) => {
    state.selfTrainingSelection = {
      Sprechen: false,
      Verstehen: false,
      Lesen: false,
      Schreiben: false,
    };
  }),
  unloadTherapistBuffer: action<StoreModel, { patientId: string }>(
    (state, payload) => {
      const { patientId } = payload;
      const key = 'results' + patientId;
      const resultsWithKey = debug(state).resultsWithKey;
      resultsWithKey[key] = [];
    },
  ),
  storeTherapistBuffer: action<
    StoreModel,
    { patientId: string; result: unknown }
  >((state, payload) => {
    const { patientId, result } = payload;
    const key = 'results' + patientId;
    const resultsWithKey = debug(state).resultsWithKey;
    if (resultsWithKey[key]) {
      resultsWithKey[key].push(result);
    } else {
      resultsWithKey[key] = [result];
    }
  }),
  // changeLockPatternState
  setPatternLockInfo: action<
    StoreModel,
    {
      appStartResumeLock: boolean;
      eigentrainingLock: boolean;
      appLockPattern: string;
    }
  >((state, payload) => {
    const { appStartResumeLock, eigentrainingLock, appLockPattern } = payload;
    state.appStartResumeLock = appStartResumeLock;
    state.eigentrainingLock = eigentrainingLock;
    state.appLockPattern = appLockPattern;
  }),
  setAppLockPattern: action<StoreModel, string>((state, payload) => {
    state.appLockPattern = payload;
  }),
  changeOnAppStartResumeLock: action<StoreModel, void>((state) => {
    state.appStartResumeLock = !state.appStartResumeLock;
  }),
  changeOnEigentrainingLock: action<StoreModel, void>((state) => {
    state.eigentrainingLock = !state.eigentrainingLock;
  }),
  changeLastVisitedPage: action<
    StoreModel,
    Route<keyof TherapistStackParamList>
  >((state, payload) => {
    state.lastVisitedPage = payload;
  }),
  // persist the storedStars and storedSessions
  persistStoredStars_StoredSessions: action<
    StoreModel,
    { sessions: unknown; stars: unknown }
  >((state, payload) => {
    const { sessions, stars } = payload;
    state.storedSessions = sessions || {};
    state.storedStars = stars || {};
  }),

  // This action is for the first screen the patient see, which is to select the sets to exercise
  chooseSetToExercise: action<StoreModel, string>((state, payload) => {
    let { sets, chosenSets } = debug(state);
    let setId = payload;

    let setsIds = sets.map((set) => set._id);
    let chosenSetsTemp = chosenSets.map((chosenSet) => chosenSet);

    chosenSetsTemp = chosenSetsTemp.filter((chosenSet) =>
      setsIds.includes(chosenSet._id),
    );

    state.chosenSets = chosenSetsTemp;

    sets = sets.map((set) => {
      if (set._id === setId) {
        set['isSetChecked'] = true;
      } else {
        set['isSetChecked'] = false;
      }
      return set;
    });

    chosenSets = sets.filter((set) => set['isSetChecked'] === true);
    state.sets = sets;
    state.chosenSets = chosenSets;
    state.currentExercisesWithTheirItems = [];
    state.chosenTasks = [];
  }),

  // This action is for choosing the tasks reading, understanding, writing, speaking
  // change it to add ExerciseType
  addTask: action<StoreModel, unknown>((state, payload: string) => {
    let exercisesDefaultOrder = [
      'Understanding',
      'Speaking',
      'Reading',
      'Writing',
    ];

    let { chosenTasks } = debug(state);
    let currentChosenTasks = chosenTasks;
    let taskName = payload;
    let currentExercisesWithTheirItems;
    let setType = debug(state).chosenSets[0].type;
    // checl if task is not yet chosen
    if (!currentChosenTasks.includes(taskName)) {
      // Add the chosen task to the array of chosen tasks
      currentChosenTasks.push(taskName);
      // check if set items are more than 4 to chunk them
      let setItems = debug(state).chosenSets[0].setItems;

      // Shuffle setItems x3
      shuffle(setItems);
      shuffle(setItems);
      shuffle(setItems);

      let chunksOfSetItems = chunk(setItems, 4);

      currentExercisesWithTheirItems =
        debug(state).currentExercisesWithTheirItems;

      let updatedExercisesWithTheirItems = chunksOfSetItems.map((items) => {
        return currentChosenTasks.map((exercise) => {
          return {
            setType,
            exerciseType: exercise,
            taskItems: shuffle(items), // shuffle items
          };
        });
      });

      // chosen tasks in the global state
      state.chosenTasks = currentChosenTasks;

      // add indexes numbers to exercises blocks to sort by them
      updatedExercisesWithTheirItems = updatedExercisesWithTheirItems.map(
        (exerciseBlock) => {
          return exerciseBlock.map((exercise) => {
            return {
              ...exercise,
              index: exercisesDefaultOrder.indexOf(exercise.exerciseType),
            };
          });
        },
      );

      // sort exercise blocks by how should the order of the exercises be before flatten it into one array of blocks
      updatedExercisesWithTheirItems = updatedExercisesWithTheirItems.map(
        (exerciseBlock) =>
          exerciseBlock.sort(function (a, b) {
            return parseFloat(a.index) - parseFloat(b.index);
          }),
      );

      // flatten the array of blocks
      // this is used later in the ExerciseProcess screen to display exercise block with their setItems
      updatedExercisesWithTheirItems = flattenDeep(
        updatedExercisesWithTheirItems,
      );
      currentExercisesWithTheirItems = updatedExercisesWithTheirItems;
    } else {
      // Remove task from current chosenTasks
      currentChosenTasks = currentChosenTasks.filter(
        (task) => task !== taskName,
      );

      // Remove task with its items from the current list of tasks with the items from sets
      currentExercisesWithTheirItems =
        debug(state).currentExercisesWithTheirItems;
      currentExercisesWithTheirItems = currentExercisesWithTheirItems.filter(
        (setItem) => setItem.exerciseType !== taskName,
      );
    }

    state.currentExercisesWithTheirItems = currentExercisesWithTheirItems;
    state.chosenTasks = currentChosenTasks;
  }),

  resetChosenSets: action<StoreModel, void>((state) => {
    state.chosenSets = [];
  }),

  // This action set the current settings of patient locally
  /**
   * @param newValue the new value to set
   * @param setType the type of the set where to change the settings
   * @param exerciseName the exercise name [Understanding, Speaking, Writing, Reading]
   * @param valueOrSetting what settings to change the value key or the setting key
   * @param textAudioSpeedSettings this key is passed only when setting the audios speed of texts
   */
  updateSettings: action<StoreModel, UpdateSettingPayload>((state, payload) => {
    // Check the set type of settings that was changed
    let setTypeSettingsToChange = payload.setType;
    let newValue = payload.newValue;
    let exerciseName = payload.exerciseName;
    let fieldToBeUpdated = payload.fieldToBeUpdated;
    let wordsetSettings = cloneDeep(debug(state).copyWordsetExerciseTypes);
    let sentencesetSettings = cloneDeep(
      debug(state).copySentencesetExerciseTypes,
    );
    let textsetSettings = cloneDeep(debug(state).copyTextsetExerciseTypes);

    if (payload.textAudioSpeedSettings) {
      state.patient.audioSpeedSetting = newValue;
      state.copyAudioSpeedSettings = newValue;
      return;
    }

    let setTypeSettings: ExerciseType[] = [];

    if (setTypeSettingsToChange === 'wordset') {
      setTypeSettings = wordsetSettings;
    } else if (setTypeSettingsToChange === 'sentenceset') {
      setTypeSettings = sentencesetSettings;
    } else if (setTypeSettingsToChange === 'textset') {
      setTypeSettings = textsetSettings;
    }

    let updatedValues = setTypeSettings.map((setting) => {
      if (setting.name === exerciseName && fieldToBeUpdated === 'value') {
        setting.value = Number(newValue);
        return setting;
      }

      if (setting.name === exerciseName && fieldToBeUpdated === 'setting') {
        setting.setting = String(newValue);
        return setting;
      }

      if (
        setting.name === exerciseName &&
        fieldToBeUpdated === 'playbackRate'
      ) {
        setting.playbackRate = Number(newValue);
        return setting;
      }

      return setting;
    });

    // Set copied settings in the global state to be saved when user clicks save button
    if (setTypeSettingsToChange === 'wordset') {
      state.hasSomethingChangedInSettings = true;
      state.copyWordsetExerciseTypes = updatedValues;
    } else if (setTypeSettingsToChange === 'sentenceset') {
      state.hasSomethingChangedInSettings = true;
      state.copySentencesetExerciseTypes = updatedValues;
    } else if (setTypeSettingsToChange === 'textset') {
      state.hasSomethingChangedInSettings = true;
      state.copyTextsetExerciseTypes = updatedValues;
    }
  }),

  // This action will save the copied settings generated when user changed the settings into patient data to be used in the exercises
  saveSettings: action<StoreModel, void>((state) => {
    if (!state.patient) {
      logError(
        `could not save patient because patient type is ${state.patient}`,
      );
      return;
    }

    if (state.copyWordsetExerciseTypes.length > 0) {
      state.patient.wordsetExerciseTypes = state.copyWordsetExerciseTypes;
    }

    if (state.copySentencesetExerciseTypes.length > 0) {
      state.patient.sentencesetExerciseTypes =
        state.copySentencesetExerciseTypes;
    }

    if (state.copyTextsetExerciseTypes.length > 0) {
      state.patient.textsetExerciseTypes = state.copyTextsetExerciseTypes;
    }

    if (state.copyAudioSpeedSettings !== state.patient.audioSpeedSetting) {
      state.patient.audioSpeedSetting = state.copyAudioSpeedSettings;
    }

    state.hasSomethingChangedInSettings = false;
  }),

  // This action will copy the current patient when settings screen is loaded
  copySettings: action<StoreModel, void>((state) => {
    if (state.patient) {
      state.copyWordsetExerciseTypes = state.patient.wordsetExerciseTypes;
      state.copySentencesetExerciseTypes =
        state.patient.sentencesetExerciseTypes;
      state.copyTextsetExerciseTypes = state.patient.textsetExerciseTypes;
      state.copyAudioSpeedSettings = state.patient.audioSpeedSetting;
    }
  }),

  //Reset all states
  resetExercisesState: action<StoreModel, void>((state) => {
    state.wrongTriesCounter = { nextLetter: '', nextWord: '', count: 0 };
    state.currentWord = '';
    state.nextWord = '';
    state.nextLetter = '';
    state.blankedLetters = [];
    state.blankedWords = [];
    state.toNextWord = false;
    state.toNextLetter = false;
    state.currentSentence = '';
    state.currentWordInArray = [];
    state.currentSentenceInArrayOfObjects = [];
    state.currentWordChoices = [];
    state.currentSentenceChoices = [];
    state.blankedLettersInSentence = [];
  }),

  // Set is patient demo
  setIsPatientDemo: action<StoreModel, boolean>((state, payload) => {
    state.isPatientDemo = payload;
    state.sets = [];
  }),

  // Set is therapist demo
  setIsTherapistDemo: action<StoreModel, boolean>((state, payload) => {
    state.isTherapistDemo = payload;
    state.sets = [];
  }),

  // Set server settings to current patient settings
  setPatientSettings: action<StoreModel, Partial<Patient>>((state, payload) => {
    let patient = cloneDeep(debug(state).patient);
    patient = extend(patient, payload);
    state.patient = patient;
    state.copyWordsetExerciseTypes = patient.wordsetExerciseTypes;
    state.copyTextsetExerciseTypes = patient.textsetExerciseTypes;
    state.copySentencesetExerciseTypes = patient.sentencesetExerciseTypes;
    state.copyAudioSpeedSettings = patient.audioSpeedSetting;
  }),

  // for demo therapist mode it starts with 1 => 2 => 3 and when it ends it goes to 0
  setToolTipNumber: action<StoreModel, number>((state, payload) => {
    state.toolTipNumber = payload;
  }),

  setFocusOnLaden: action<StoreModel, boolean>((state, payload) => {
    state.focusOnLaden = payload;
  }),

  //Set server settings to current patient settings
  setPatientSets: action<StoreModel, unknown[]>((state, payload) => {
    // Check set chosen sets automatically
    let chosenSets = debug(state).chosenSets;
    let chosenSetsIds = chosenSets.map((set) => set._id);
    state.sets = payload;
    state.chosenSets = [];

    payload.map((set) => {
      if (chosenSetsIds.includes(set._id)) {
        set.isSetChecked = true;
        state.chosenSets = [set];
      }
    });

    if (payload.length > 0 && state.chosenSets.length === 0) {
      let lastSet = payload[payload.length - 1];
      lastSet.isSetChecked = true;
      state.chosenSets = [lastSet];
    }
  }),

  // Set server last synced sets
  setLastSyncedSets: action<StoreModel, unknown>((state, payload) => {
    state.lastSyncedSets = payload;
  }),

  // Set maximum exercises items
  setMaxExerciseItems: action<StoreModel, number>((state, payload) => {
    state.maxExerciseItems = payload;
  }),

  // Set practiceSessions with key
  setPracticeSessionsWithKey: action<StoreModel, unknown>((state, payload) => {
    const userId = state.signedInUser?.user?._id;
    const uniqueKey = `practiceSessions${userId}`;
    if (payload === 'reset') {
      state.practiceSessionsWithKey[uniqueKey] = [];
    } else {
      let currentPracticeSessionsWithKey =
        debug(state).practiceSessionsWithKey[uniqueKey];
      currentPracticeSessionsWithKey.push(payload.data);
      state.practiceSessionsWithKey[uniqueKey] = currentPracticeSessionsWithKey;
    }
  }),

  // Initialize practice session with key and results with key arrays
  initializeResultsWithKeyAndPracticeSessionWithKey: action<StoreModel>(
    (proxyState) => {
      const state = debug(proxyState);
      const userId = state.signedInUser?.user?._id;
      const resultsUniqueKey = `results${userId}`;
      const practiceSessionsUniqueKey = `practiceSessions${userId}`;
      if (
        !Object.prototype.hasOwnProperty.call(
          resultsUniqueKey,
          state.resultsWithKey,
        )
      ) {
        state.resultsWithKey = { [resultsUniqueKey]: [] };
      }
      if (
        !Object.prototype.hasOwnProperty.call(
          state.practiceSessionsWithKey,
          practiceSessionsUniqueKey,
        )
      ) {
        state.practiceSessionsWithKey = { [practiceSessionsUniqueKey]: [] };
      }
    },
  ),

  // Set Practice Session Track
  setPracticeSessionTrack: action<StoreModel, unknown>((state, payload) => {
    if (payload === 'reset') {
      state.trackPracticeSession = {
        ...trackPracticeSessionDefault,
      };
    } else {
      let currentPracticeSessionObject = debug(state).trackPracticeSession;
      let updatedPracticeSessionObject = {
        ...currentPracticeSessionObject,
        ...payload,
      };

      state.trackPracticeSession = updatedPracticeSessionObject;
    }
  }),

  // set current practice session time
  setCurrentPracticeSessionDuration: action<StoreModel, number>(
    (state, payload) => {
      state.currentPracticeSessionDuration = payload;
    },
  ),

  // set star container data
  setStarContainer: action<StoreModel, unknown>((state, payload) => {
    if (payload === 'reset') {
      state.starContainer = {};
    } else {
      state.starContainer = {
        ...state.starContainer,
        container: { ...state.starContainer.container, ...payload.container },
      };
    }
  }),

  // Set stay lastLoginEmail
  setLastLoginEmail: action<StoreModel, string>((state, payload) => {
    state.lastLoginEmail = payload;
  }),

  // Set last idle timestamp
  setLastIdleDate: action<StoreModel, Date | null>((state, payload) => {
    state.lastIdleDate = payload;
  }),

  // Set stay logged In
  setStayLoggedIn: action<StoreModel, boolean>((state, payload) => {
    state.stayLoggedIn = payload;
  }),

  setTherapistWebAppLogin: action<StoreModel, boolean>((state, payload) => {
    state.therapistWebAppLogin = payload;
  }),

  // Set is login screen
  setIsLoginScreen: action<StoreModel, boolean>((state, payload) => {
    state.isLoginScreen = payload;
  }),

  // after login don't show the lock screen while the user is in the foreground, only after going to the background
  setShowLockPatternScreen: action<StoreModel, boolean>((state, payload) => {
    state.showLockPatternScreen = payload;
  }),

  // Triggers on therapist:
  // - when the refresh button is pressed
  // - when we need to download the exercises before starting the "eigeintraining"
  triggerRefresh: action<StoreModel, void>((state) => {
    if (!state.refreshTogglerState) {
      state.refreshTogglerState = true;
    }
    state.refresh = !state.refresh;
  }),

  setUserConsents: action<StoreModel, UserConsent[]>((state, payload) => {
    state.userConsents = isFeatureEnabled(FeatureFlag.EPA_ENABLED)
      ? payload
      : payload.filter((p) => p.name !== 'WriteToEpa');
  }),

  setDownloadPatients: action<StoreModel, unknown>((state, payload) => {
    state.downloadPatients = payload;
  }),

  setUserLeftExerciseBeforeFinishing10Minutes: action<
    StoreModel,
    'reset' | void
  >((state, payload) => {
    if (payload === 'reset') {
      state.userLeftExerciseBeforeFinishing10Minutes = false;
    } else {
      state.userLeftExerciseBeforeFinishing10Minutes = true;
    }
  }),

  setLastTimeDurationUserExercisedBeforeLeavingTheExerciseWithoutFinishing10Minutes:
    action<StoreModel, unknown>((state, payload) => {
      if (payload === 'reset') {
        state.lastTimeDurationUserExercisedBeforeLeavingTheExerciseWithoutFinishing10Minutes = 0;
      } else {
        state.lastTimeDurationUserExercisedBeforeLeavingTheExerciseWithoutFinishing10Minutes =
          payload;
      }
    }),

  setResultsModalWasShown: action<StoreModel, 'reset' | void>(
    (state, payload) => {
      if (payload === 'reset') {
        state.resultModalWasShown = false;
      } else {
        state.resultModalWasShown = true;
      }
    },
  ),

  setExercisedDuration: action<StoreModel, 'reset' | number>(
    (state, payload) => {
      if (payload === 'reset') {
        state.exercisedDuration = 0;
      } else {
        state.exercisedDuration = payload;
      }
    },
  ),

  setRemainingTodayExerciseTimeInMinutes: action<StoreModel, 'reset' | number>(
    (state, payload) => {
      if (payload === 'reset') {
        state.remainingTodayExerciseTimeInMinutes = 0;
      } else {
        state.remainingTodayExerciseTimeInMinutes = payload;
      }
    },
  ),
  showNavigateToHomeStarsPopUp: action<StoreModel, 'reset' | void>(
    (state, payload) => {
      if (payload === 'reset') {
        state.showNavigatingToHomeStarsPopUp = false;
      } else {
        state.showNavigatingToHomeStarsPopUp = true;
      }
    },
  ),
  setStarsPopUp: action<StoreModel, 'reset' | void>((state, payload) => {
    if (payload === 'reset') {
      state.showStarsPopUp = false;
    } else {
      state.showStarsPopUp = true;
    }
  }),

  setSubscriptions: action<StoreModel, Subscription[]>((state, payload) => {
    state.subscriptions = payload;
  }),

  setPermissions: action<StoreModel, unknown>((state, payload) => {
    state.permissions = payload;
  }),

  setShowConsentModal: action<StoreModel, unknown>((state, payload) => {
    state.showConsentModal = payload;
  }),

  toggleTherapistCanSeeResults: action<StoreModel, boolean>(
    (state, payload) => {
      state.signedInUser.user = {
        ...debug(state).signedInUser.user,
        therapistCanSeeResults: payload,
      };
    },
  ),

  setUserInsuranceId: action((state, idpInsuranceId) => {
    state.signedInUser = {
      ...state.signedInUser,
      user: {
        ...state.signedInUser.user,
        idpInsuranceId,
      },
    };
  }),

  setUserEpa: action<StoreModel, UserEpa | undefined>((state, userEpa) => {
    state.userEpa = userEpa;
  }),

  setUserIsAutoUploadEpa: action<StoreModel, boolean>((state, autoUpload) => {
    if (state.userEpa) {
      state.userEpa.autoUpload = autoUpload;
    }
  }),

  resetGlobalPopupState: action<StoreModel, void>((state) => {
    state.showGlobalPopup = false;
    state.headerText = '';
    state.bodyText = '';
    state.leftButton = '';
    state.rightButton = '';
  }),

  setGlobalPopupState: action<StoreModel, unknown>((state, payload) => {
    const { headerText, bodyText, leftButton, rightButton } = payload;
    state.showGlobalPopup = true;
    state.headerText = headerText;
    state.bodyText = bodyText;
    state.leftButton = leftButton;
    state.rightButton = rightButton;
  }),

  onFirstMountStoreSetup: action<StoreModel, unknown>((state, payload) => {
    const {
      appStartResumeLock,
      eigentrainingLock,
      appLockPattern,
      patients,
      subscriptions,
      permissions,
    } = payload;
    state.appStartResumeLock = appStartResumeLock;
    state.eigentrainingLock = eigentrainingLock;
    state.appLockPattern = appLockPattern;
    state.downloadPatients = patients;
    state.subscriptions = subscriptions;
    state.permissions = permissions;
  }),

  // checking for app versions
  setShowAppNeedToBeUpdatedModal: action<StoreModel, boolean>(
    (state, payload) => {
      state.showAppNeedToBeUpdatedModal = payload;
    },
  ),

  setShowCriticalAppUpdateModal: action<StoreModel, boolean>(
    (state, payload) => {
      state.showCriticalAppUpdateModal = payload;
    },
  ),

  setShowLoadingSpinner: action<StoreModel, boolean>((state, payload) => {
    state.showLoadingSpinner = payload;
  }),

  setPreviewState: action<
    StoreModel,
    { patientId: string; exerciseClass: string; exercises: any[] }
  >((state, payload) => {
    const { patientId, exerciseClass, exercises } = payload;
    const existing = state.previewState.find(
      (item) => item.patientId === patientId,
    );

    if (existing) {
      existing.exercisesByClass[exerciseClass] = exercises;
    } else {
      state.previewState.push({
        patientId,
        exercisesByClass: { [exerciseClass]: exercises },
      });
    }
  }),

  ...authLocalActions,
};

export default localActions;
